import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { showErrorToast } from '@app/components/CommonToast';
import { MASTER_CODE_TYPE } from '@app/consts/master_code_consts';
import { useFormController } from '@app/hooks/useFormController';
import { MessageUtils } from '@app/modules/errors';
import { commonApiService } from '@app/services/commonApi';
import * as _ from 'lodash-es';
import { Button } from 'primereact/button';
import { Calendar, CalendarMonthNavigatorTemplateEvent, CalendarYearNavigatorTemplateEvent } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';

import './FormDatePicker.scss';

import { DateFormat } from '@app/utils/DateUtils';
import { useTranslation } from 'react-i18next';

const DEFAULT_MIN_YEAR = 1990;
const DEFAULT_MAX_YEAR = 2050;

type FormDatePickerProps = {
  classNameError?: string;
  name: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  inputOnly?: boolean;
  dateFormat?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  value?: Date | null;
  inputStyle?: React.CSSProperties;
  hasResetBtn?: boolean;
  keepInvalid?: boolean;
  timeZone?: string;
  onChange?: (value: Date | null) => void;
};

const FormDatePicker: React.FC<FormDatePickerProps> = ({
  classNameError,
  name,
  placeholder,
  label,
  inputOnly = false,
  required = false,
  disabled = false,
  minDate,
  maxDate,
  value,
  inputStyle,
  hasResetBtn = true,
  keepInvalid = false,
  timeZone = 'Asia/Tokyo',
  onChange,
}) => {
  const { field, fieldState } = useFormController({ name });
  const { i18n, t } = useTranslation();
  const calendarRef = useRef<Calendar>(null);
  const uid = useId();
  const inputId = `${name}-${uid}`;
  const [viewDate, setViewDate] = useState<Date>(new Date());
  const [minYear, setMinYear] = useState<number | null>(null);
  const [maxYear, setMaxYear] = useState<number | null>(null);

  /** DD — BM0046 項目コード: "001" = display start year, "002" = display end year */
  const CALENDAR_YEAR_RANGE_ITEM_CODE = {
    START_YEAR: '001',
    END_YEAR: '002',
  };

  // [BM0046] Fetch Min Year / Max Year for the Calendar Picker year boundary.
  useEffect(() => {
    const fetchCalendarYearRange = async () => {
      try {
        const response = await commonApiService.getMasterCodeOptions({
          masterTypeCodes: [MASTER_CODE_TYPE.CALENDAR_YEAR_RANGE],
        });
        const items = _.pick(response?.data?.content, [MASTER_CODE_TYPE.CALENDAR_YEAR_RANGE]);
        const yearsRange = items[MASTER_CODE_TYPE.CALENDAR_YEAR_RANGE] ?? [];

        if (response.success && yearsRange.length > 0) {
          const startYearItem = _.find(yearsRange, (item) => item.code === CALENDAR_YEAR_RANGE_ITEM_CODE.START_YEAR);
          const endYearItem = _.find(yearsRange, (item) => item.code === CALENDAR_YEAR_RANGE_ITEM_CODE.END_YEAR);
          const parsedStartYear = Number.parseInt(startYearItem?.nameJA ?? '', 10);
          const parsedEndYear = Number.parseInt(endYearItem?.nameJA ?? '', 10);
          setMinYear(_.isNaN(parsedStartYear) ? null : parsedStartYear);
          setMaxYear(_.isNaN(parsedEndYear) ? null : parsedEndYear);
        }
      } catch (error: any) {
        showErrorToast(MessageUtils.formatApiMessage(error) || t(error?.code, { ns: 'messages' }));
      }
    };

    fetchCalendarYearRange();
  }, [t]);

  // Effective min/max date — intersects the BM0046 boundary with any minDate/maxDate passed by the parent.
  const effectiveMinDate = useMemo(() => {
    const masterMinDate = minYear !== null ? new Date(minYear, 0, 1) : undefined;
    if (masterMinDate && minDate) return masterMinDate > minDate ? masterMinDate : minDate;
    return masterMinDate ?? minDate;
  }, [minYear, minDate]);

  const effectiveMaxDate = useMemo(() => {
    const masterMaxDate = maxYear !== null ? new Date(maxYear, 11, 31) : undefined;
    if (masterMaxDate && maxDate) return masterMaxDate < maxDate ? masterMaxDate : maxDate;
    return masterMaxDate ?? maxDate;
  }, [maxYear, maxDate]);

  const yearRange = `${minYear ?? DEFAULT_MIN_YEAR}:${maxYear ?? DEFAULT_MAX_YEAR}`;

  /**
   * Convert a UTC Date to a "display Date" whose local y/m/d values
   * match what the date looks like in the target timezone.
   * e.g. 2026-03-08T15:00:00Z  →  2026-03-09 00:00:00 (JST)
   */
  const toTzDisplay = (date: Date | null | undefined): Date | null => {
    if (!date) return null;
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const get = (type: string) => Number.parseInt(parts.find((p) => p.type === type)?.value ?? '0');
    return new Date(get('year'), get('month') - 1, get('day'));
  };

  /**
   * Convert a Calendar-selected Date (local midnight) back to a UTC midnight Date.
   * formatDateForApi() will then format it correctly in the target timezone.
   * e.g. user picks 2026-03-09 → new Date('2026-03-09T00:00:00Z')
   */
  const fromTzDisplay = (date: Date | null | undefined): Date | null => {
    if (!date) return null;
    const iso = [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0'),
    ].join('-');
    return new Date(`${iso}T00:00:00Z`);
  };

  useEffect(() => {
    if (!field.value) {
      setViewDate(new Date());
    }
  }, [field.value]);

  const handleOnHide = () => {
    // Force blur when calendar panel closes using native DOM API
    setTimeout(() => {
      const inputElement = document.getElementById(inputId) as HTMLInputElement;
      if (inputElement && document.activeElement === inputElement) {
        inputElement.blur();
        // Remove focus completely from the component
        if (document.body) {
          document.body.focus();
        }
      }
    }, 100);
  };

  const handleReset = () => {
    field.onChange(null);
  };

  const monthNavigatorTemplate = (options: CalendarMonthNavigatorTemplateEvent) => (
    <Dropdown
      value={options.value}
      options={[...options.options].reverse()}
      onChange={(e) => {
        options.onChange(e.originalEvent!, e.value);
      }}
      className="custom-nav-dropdown ml-1"
      panelClassName="custom-nav-panel"
      style={{ lineHeight: 1, fontSize: '13px' }}
      scrollHeight="120px"
      pt={{ panel: { onMouseDown: (e) => e.stopPropagation() } }}
    />
  );

  const yearNavigatorTemplate = (options: CalendarYearNavigatorTemplateEvent) => (
    <Dropdown
      value={options.value}
      options={[...options.options].reverse()}
      onChange={(e) => {
        options.onChange(e.originalEvent!, e.value);
      }}
      className="custom-nav-dropdown"
      panelClassName="custom-nav-panel"
      style={{ lineHeight: 1, fontSize: '13px' }}
      scrollHeight="120px"
      pt={{ panel: { onMouseDown: (e) => e.stopPropagation() } }}
    />
  );

  return (
    <div className={`flex flex-column field-name custom-data-picker relative ${name}`}>
      {label && !inputOnly && (
        <label htmlFor={inputId} className="block body-sx fw-400 mb-1">
          {label}
          {required && <span style={{ color: 'var(--color-danger)', marginLeft: '4px' }}>*</span>}
        </label>
      )}

      <Calendar
        {...field}
        ref={calendarRef}
        inputId={inputId}
        value={toTzDisplay(value !== undefined ? value : field.value)}
        onChange={(e) => {
          const converted = fromTzDisplay(e.value as Date | null);
          field.onChange(converted);
          onChange?.(converted);
        }}
        onHide={handleOnHide}
        placeholder={placeholder}
        showIcon={false}
        disabled={disabled}
        minDate={effectiveMinDate}
        maxDate={effectiveMaxDate}
        className={fieldState.invalid ? 'p-invalid' : ''}
        inputStyle={inputStyle}
        keepInvalid={keepInvalid}
        monthNavigator
        yearNavigator
        monthNavigatorTemplate={monthNavigatorTemplate}
        yearNavigatorTemplate={yearNavigatorTemplate}
        locale={i18n.language}
        dateFormat={DateFormat.YEAR_MONTH_DAY_V2}
        viewDate={viewDate}
        onViewDateChange={(e) => setViewDate(e.value as Date)}
        panelStyle={{ width: '252px' }}
        yearRange={yearRange}
      />
      {hasResetBtn && field.value && !disabled && (
        <Button
          icon="pi pi-times"
          className={'p-button-text p-button-rounded button-clear-date ' + (fieldState.invalid ? 'p-err' : '')}
          onClick={handleReset}
          type="button"
          aria-label="Clear date"
        />
      )}
      {fieldState.invalid && (
        <small className={`p-error body-sx ${classNameError ?? ''}`}>{fieldState?.error?.message}</small>
      )}
    </div>
  );
};

export { FormDatePicker };
